<script setup lang="ts">
import { useElementBounding, useEventListener } from '@vueuse/core';
import { Dayjs } from 'dayjs';
import type {
  TDateInput,
  TMode,
  TRangeValue,
} from '~/interfaces/components/date-picker';
import useDatePicker from '~/composables/inputs/date-picker/useDatePicker';

const props = withDefaults(
  defineProps<{
    appendTo?: string | HTMLElement;
    bgColor?: string;
    closeOnSelect?: boolean;
    disabled?: boolean;
    endDatePlaceholder?: string;
    format?: string;
    hideResetOverRide?: boolean;
    id?: string;
    maxDate?: TDateInput;
    minDate?: TDateInput;
    mode?: TMode;
    placeholder?: string;
    showFooterConfirm?: boolean;
    startDatePlaceholder?: string;
    zIndex?: number;
  }>(),
  {
    appendTo: 'body',
    bgColor: 'bg-base',
    closeOnSelect: true,
    disabled: false,
    endDatePlaceholder: 'end-date',
    format: 'DD/MM/YYYY',
    hideResetOverRide: false,
    id: 'date-picker',
    maxDate: undefined,
    minDate: undefined,
    mode: 'single',
    placeholder: 'pick-a-date',
    showFooterConfirm: true,
    startDatePlaceholder: 'start-date',
    zIndex: 1102,
  },
);

const emits = defineEmits<{
  (e: 'cancel'): void;
  (e: 'confirm', value: { startDate: Dayjs; endDate: Dayjs } | Dayjs): void;
  (e: 'input-change', value: TDateInput | TRangeValue): void;
  (e: 'reset'): void;
}>();

const modelValue = defineModel<TDateInput | TRangeValue>('date');

const {
  canGoNextMonth,
  canGoPrevMonth,
  canGoNextYear,
  canGoPrevYear,
  dayNames,
  displayRangeEnd,
  displayRangeStart,
  displaySingle,
  grid,
  isRange,
  modelRange,
  modelSingle,
  monthNames,
  monthOptions,
  open,
  showReset,
  viewMode,
  viewMonth,
  viewYear,
  yearOptions,
  chooseMonth,
  chooseYear,
  closePopup,
  confirmRange,
  goNextMonth,
  goPrevMonth,
  goNextYear,
  goPrevYear,
  isSelectedEnd,
  isSelectedStart,
  isWithinSelected,
  onDayClick,
  resetCalendar,
  resetSelection,
  togglePopup,
} = useDatePicker({
  closeOnSelect: props.closeOnSelect,
  endDatePlaceholder: props.endDatePlaceholder!,
  format: props.format!,
  maxDate: props.maxDate,
  minDate: props.minDate,
  mode: props.mode,
  modelValue,
  placeholder: props.placeholder!,
  startDatePlaceholder: props.startDatePlaceholder!,
});

const root = ref<HTMLElement>();
const { top, left, height, width, update } = useElementBounding(root, {
  immediate: false,
  reset: true,
  updateTiming: 'sync',
});
const panelStyle = computed<{ [key: string]: string | number }>(() => ({
  position: 'fixed',
  top: `${(top.value || 0) + (height.value || 0) + 4}px`,
  left: `${left.value || 0}px`,
  width: `${width.value || 320}px`,
  zIndex: props.zIndex,
}));

provide<Ref<HTMLElement>>('datePickerRoot', root);

const onConfirm = (v: Dayjs | { startDate: Dayjs; endDate: Dayjs }) => {
  emits('confirm', v);
  emits('input-change', v);
  closePopup();
};
const onReset = (cancel = false, emit = true) => {
  if (!cancel && emit) emits('reset');
  else if (cancel) {
    togglePopup(false);
    if (emit) emits('cancel');
  }
  if (isRange.value) modelRange.value = { startDate: null, endDate: null };
  else modelSingle.value = null;
  resetCalendar();
};
useEventListener('keydown', (e: KeyboardEvent) => {
  if (!open.value) return;
  if (e.key === 'Escape') onReset(true, true);
});

watch(
  () => open.value,
  (newVal) => {
    if (newVal) update();
  },
);
</script>

<template>
  <div
    ref="root"
    class="relative w-full capitalize"
    :class="{ 'cursor-pointer': !disabled, 'cursor-not-allowed': disabled }"
  >
    <DesignSystemInputsDatePickerInputs
      :id="id"
      :bg-color="bgColor"
      :disabled="disabled"
      :display-single="displaySingle"
      :display-range-end="displayRangeEnd"
      :display-range-start="displayRangeStart"
      :mode="mode"
      :open="open"
      :show-reset="showReset"
      :hide-reset-over-ride="hideResetOverRide"
      :highlight-end="
        (!!modelRange.startDate && !modelRange.endDate) ||
        (!!modelRange.startDate && !!modelRange.endDate)
      "
      :highlight-start="
        (!!modelRange.startDate && !!modelRange.endDate) ||
        (!modelRange.startDate && !!modelRange.endDate)
      "
      :highlight-single="!!modelSingle && !isRange"
      @reset="resetSelection(onReset)"
      @toggle="togglePopup"
    />
    <DesignSystemInputsDatePickerPanel
      v-if="open"
      :append-to="appendTo"
      :bg-color="bgColor"
      :can-go-next-month="canGoNextMonth"
      :can-go-prev-month="canGoPrevMonth"
      :can-go-next-year="canGoNextYear"
      :can-go-prev-year="canGoPrevYear"
      :close-on-select="closeOnSelect"
      :day-names="dayNames"
      :disabled="disabled"
      :format="format"
      :grid="grid"
      :is-range="isRange"
      :is-selected-end="isSelectedEnd"
      :is-selected-start="isSelectedStart"
      :is-within-selected="isWithinSelected"
      :model-range="modelRange"
      :model-single="modelSingle"
      :month-names="monthNames"
      :month-options="monthOptions"
      :open="open"
      :panel-style="panelStyle"
      :show-footer-confirm="showFooterConfirm"
      :view-mode="viewMode"
      :view-month="viewMonth"
      :view-year="viewYear"
      :year-options="yearOptions"
      @cancel="onReset(true)"
      @choose-month="chooseMonth"
      @choose-year="chooseYear"
      @clear="onReset(false, false)"
      @close="togglePopup(false)"
      @confirm="() => confirmRange(onConfirm)"
      @day-click="onDayClick($event, { emitConfirm: onConfirm })"
      @next="goNextMonth"
      @prev="goPrevMonth"
      @next-year="goNextYear"
      @prev-year="goPrevYear"
      @toggle-month="viewMode = viewMode === 'months' ? 'days' : 'months'"
      @toggle-year="viewMode = viewMode === 'years' ? 'days' : 'years'"
    />
  </div>
</template>
